import { useState, useRef } from 'react'
import { sanitizeInput } from '../utils/sanitizeInput'
import { uploadINEImage } from '../services/kycUpload'
import { useAuthStore } from '../store/authStore'
import { User, Mail, Phone, Calendar, Shield, Bell, Lock } from 'lucide-react'
import toast from 'react-hot-toast'

export default function Profile() {
  const { userData, updateUserData, updatePassword } = useAuthStore()
  const [ineFile, setIneFile] = useState(null)
  const [ineUploading, setIneUploading] = useState(false)
  const [ineUrl, setIneUrl] = useState(userData?.kyc?.ineUrl || '')
  const ineInputRef = useRef()

  const handleINEUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    // Log para depuración
    import('../services/firebase').then(({ auth }) => {
      // eslint-disable-next-line no-console
      console.log('userData.id:', userData?.id, 'auth.currentUser.uid:', auth.currentUser?.uid)
    })
    setIneUploading(true)
    try {
      const url = await uploadINEImage(file, userData?.id)
      setIneUrl(url)
      await updateUserData({ kyc: { ...userData.kyc, ineUrl: url, documentSubmitted: true, rejectionReason: null, rejectedAt: null } })
      toast.success('INE subida correctamente')
    } catch (err) {
      const msg = err?.message || err?.code || 'Error al subir INE'
      toast.error('Error al subir INE: ' + msg)
      // eslint-disable-next-line no-console
      console.error('Firebase Storage error:', err)
    }
    setIneUploading(false)
  }
  const [activeTab, setActiveTab] = useState('personal')
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    fullName: userData?.fullName || '',
    phone: userData?.phone || '',
  })
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  })

  const handleUpdateProfile = async (e) => {
    e.preventDefault()
    const sanitizedData = {
      fullName: sanitizeInput(formData.fullName),
      phone: sanitizeInput(formData.phone),
    }
    const result = await updateUserData(sanitizedData)
    if (result.success) {
      setIsEditing(false)
    }
  }

  const handleUpdatePassword = async (e) => {
    e.preventDefault()
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Las contraseñas no coinciden')
      return
    }
    const result = await updatePassword(passwordData.newPassword)
    if (result.success) {
      setPasswordData({ newPassword: '', confirmPassword: '' })
    }
  }

  const tabs = [
    { id: 'personal', label: 'Información Personal', icon: User },
    { id: 'security', label: 'Seguridad', icon: Shield },
    { id: 'notifications', label: 'Notificaciones', icon: Bell },
  ]

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-kiniela-bg min-h-screen">
      <h1 className="text-3xl font-bold mb-6 text-kiniela-gold">Mi Perfil</h1>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="card bg-kiniela-card border border-kiniela-border">
            <div className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition ${
                      activeTab === tab.id
                        ? 'bg-kiniela-accent text-kiniela-text font-medium border border-kiniela-accent'
                        : 'hover:bg-kiniela-bg text-kiniela-text'
                    }`}
                  >
                    <Icon size={20} />
                    <span>{tab.label}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          {activeTab === 'personal' && (
            <div className="card bg-kiniela-card border border-kiniela-border">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-kiniela-gold">Información Personal</h2>
                {!isEditing && (
                  <button onClick={() => setIsEditing(true)} className="btn bg-kiniela-accent text-kiniela-text hover:bg-kiniela-accentHover">
                    Editar
                  </button>
                )}
              </div>

              <form onSubmit={handleUpdateProfile} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-kiniela-text mb-1">
                    Nombre de Usuario
                  </label>
                  <div className="input bg-kiniela-bg cursor-not-allowed text-kiniela-text border border-kiniela-border">
                    {userData?.username}
                  </div>
                  <p className="text-xs text-kiniela-muted mt-1">El nombre de usuario no puede cambiarse</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-kiniela-text mb-1">
                    Nombre Completo
                  </label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: sanitizeInput(e.target.value) })}
                    className="input bg-kiniela-bg text-kiniela-text border border-kiniela-border"
                    disabled={!isEditing}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-kiniela-text mb-1">
                    Email
                  </label>
                  <div className="input bg-kiniela-bg cursor-not-allowed text-kiniela-text border border-kiniela-border">
                    {userData?.email}
                  </div>
                  <p className="text-xs text-kiniela-text mt-1">El email no puede cambiarse</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-kiniela-text mb-1">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: sanitizeInput(e.target.value) })}
                    className="input bg-kiniela-bg text-kiniela-text border border-kiniela-border"
                    disabled={!isEditing || userData?.phoneVerified}
                  />
                  {userData?.phoneVerified && (
                    <p className="text-xs text-kiniela-success mt-1 flex items-center gap-1">
                      <Phone size={14} /> Teléfono verificado, no editable
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-kiniela-text mb-1">
                    Fecha de Nacimiento
                  </label>
                  <div className="input bg-kiniela-bg cursor-not-allowed text-kiniela-text border border-kiniela-border">
                    {userData?.dateOfBirth}
                  </div>
                </div>

                {isEditing && (
                  <div className="flex gap-4">
                    <button type="submit" className="btn bg-kiniela-cta text-kiniela-text hover:bg-kiniela-ctaHover">
                      Guardar Cambios
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditing(false)
                        setFormData({
                          fullName: userData?.fullName || '',
                          phone: userData?.phone || '',
                        })
                      }}
                      className="btn bg-kiniela-card text-kiniela-text hover:bg-[#333333] border border-kiniela-border"
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </form>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="card bg-kiniela-card border border-kiniela-border">
              <h2 className="text-2xl font-bold mb-6 text-kiniela-gold">Seguridad</h2>

              <form onSubmit={handleUpdatePassword} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-kiniela-text mb-1">
                    Nueva Contraseña
                  </label>
                  <input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    className="input bg-kiniela-bg text-kiniela-text border border-kiniela-border"
                    placeholder="••••••••"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-kiniela-text mb-1">
                    Confirmar Nueva Contraseña
                  </label>
                  <input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    className="input bg-kiniela-bg text-kiniela-text border border-kiniela-border"
                    placeholder="••••••••"
                  />
                </div>

                <button type="submit" className="btn bg-kiniela-cta text-kiniela-text hover:bg-kiniela-ctaHover">
                  Cambiar Contraseña
                </button>
              </form>

              <div className="mt-8 p-4 bg-kiniela-bg rounded-lg border border-kiniela-border">
                <h3 className="font-semibold mb-2 text-kiniela-gold">Verificación KYC</h3>
                <p className="text-sm text-kiniela-text mb-2">
                  {userData?.kyc?.verified
                    ? 'Tu identidad ha sido verificada ✓'
                    : 'Debes verificar tu identidad para poder retirar fondos.'}
                </p>
                <div className="mb-2 text-xs text-kiniela-cta font-medium">
                  El nombre en tu INE debe coincidir exactamente con tu nombre de usuario: <span className="font-bold">{userData?.fullName}</span>
                </div>
                {!userData?.kyc?.verified && (
                  <div className="space-y-2">
                    {userData?.kyc?.rejectionReason && (
                      <div className="bg-red-900/20 border border-red-500/50 rounded-lg p-3 mb-3">
                        <p className="text-sm text-red-400 font-semibold mb-1">Documento rechazado</p>
                        <p className="text-xs text-red-300">Motivo: {userData.kyc.rejectionReason}</p>
                        <p className="text-xs text-kiniela-muted mt-1">Por favor, sube un nuevo documento corrigiendo los errores mencionados.</p>
                      </div>
                    )}
                    {!(ineUploading || !!ineUrl || userData?.kyc?.documentSubmitted) && (
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        ref={ineInputRef}
                        onChange={handleINEUpload}
                        className="block w-full text-sm text-kiniela-text file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-kiniela-accent file:text-kiniela-text hover:file:bg-[#16A34A]"
                        disabled={ineUploading}
                      />
                    )}
                    {ineUploading && <div className="text-xs text-kiniela-muted">Subiendo INE...</div>}
                    {ineUrl && (
                      <div className="text-xs text-kiniela-success">Documento subido: <a href={ineUrl} target="_blank" rel="noopener noreferrer" className="underline">Ver archivo</a></div>
                    )}
                    {(!!ineUrl || userData?.kyc?.documentSubmitted) && (
                      <div className="text-xs text-kiniela-text">Ya has subido un documento. Si necesitas cambiarlo, contacta soporte.</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="card bg-kiniela-card border border-kiniela-border">
              <h2 className="text-2xl font-bold mb-6 text-kiniela-gold">Preferencias de Notificaciones</h2>

              <div className="space-y-4">
                <label className="flex items-center justify-between p-4 border border-kiniela-border rounded-lg cursor-pointer hover:bg-kiniela-bg">
                  <div>
                    <p className="font-medium text-kiniela-text">Notificaciones por Email</p>
                    <p className="text-sm text-kiniela-text">Recibe actualizaciones por correo electrónico</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={userData?.notifications?.email}
                    onChange={(e) => updateUserData({ 
                      notifications: { ...userData.notifications, email: e.target.checked }
                    })}
                    className="w-5 h-5"
                  />
                </label>

                <label className="flex items-center justify-between p-4 border border-kiniela-border rounded-lg cursor-pointer hover:bg-kiniela-bg">
                  <div>
                    <p className="font-medium text-kiniela-text">Notificaciones por SMS</p>
                    <p className="text-sm text-kiniela-text">Recibe mensajes de texto</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={userData?.notifications?.sms}
                    onChange={(e) => updateUserData({ 
                      notifications: { ...userData.notifications, sms: e.target.checked }
                    })}
                    className="w-5 h-5"
                  />
                </label>

                <label className="flex items-center justify-between p-4 border border-kiniela-border rounded-lg cursor-pointer hover:bg-kiniela-bg">
                  <div>
                    <p className="font-medium text-kiniela-text">Notificaciones en la Plataforma</p>
                    <p className="text-sm text-kiniela-text">Alertas dentro de la aplicación</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={userData?.notifications?.platform}
                    onChange={(e) => updateUserData({ 
                      notifications: { ...userData.notifications, platform: e.target.checked }
                    })}
                    className="w-5 h-5"
                  />
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
